import { type ReactNode } from 'react';
import {
  Button,
  ConfirmationDialog,
  CopyIdButton,
  EntityIcon,
  MetaDefault,
  ObjectEditPageLayout,
  isSeverityReasonMissing,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';

import { useFindingPublishConfirm } from '../../hooks/useFindingPublishConfirm';
import { useCopilotFindingEditProcessingStore } from '../../store/copilotFindingEditProcessingStore';
import { FindingDetailsEditForm } from './FindingDetailsEditForm';
import type { FindingDetailsEditFormProps } from './findingEditForm.types';

export type FindingDetailsEditViewProps = {
  eyebrow: string;
  title: string;
  entityTitle: string;
  findingId: string;
  hasUnsavedChanges: boolean;
  hasPendingSuggestions: boolean;
  isSaving: boolean;
  isPublished: boolean;
  isPublishing?: boolean;
  isArchived?: boolean;
  publishLabel: string;
  saveChangesLabel: string;
  resetLabel: string;
  cancelLabel: string;
  unsavedChangesLabel: string;
  allChangesSavedLabel: string;
  meta: ReactNode;
  suggestionReviewBar: ReactNode;
  onReset: () => void;
  onCancel: () => void;
  onSave: () => void;
  onPublish: () => void | Promise<void>;
  onStopCopilotWorking: () => void;
  editFormProps: FindingDetailsEditFormProps;
};

export function FindingDetailsEditView({
  eyebrow,
  title,
  entityTitle,
  findingId,
  hasUnsavedChanges,
  hasPendingSuggestions,
  isSaving,
  isPublished,
  isPublishing,
  isArchived,
  publishLabel,
  saveChangesLabel,
  resetLabel,
  cancelLabel,
  unsavedChangesLabel,
  allChangesSavedLabel,
  meta,
  suggestionReviewBar,
  onReset,
  onCancel,
  onSave,
  onPublish,
  onStopCopilotWorking,
  editFormProps,
}: FindingDetailsEditViewProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const isProcessing = useCopilotFindingEditProcessingStore(
    (state) => state.processingFindingId === findingId,
  );
  const editingFieldCount = useCopilotFindingEditProcessingStore(
    (state) => state.editingFieldCount,
  );
  const {
    isPublishConfirmOpen,
    setIsPublishConfirmOpen,
    handlePublishConfirmOpenChange,
    handleConfirmPublish,
  } = useFindingPublishConfirm({ onPublish });
  const isSaveDisabled =
    !hasUnsavedChanges ||
    isSaving ||
    isProcessing ||
    hasPendingSuggestions ||
    isSeverityReasonMissing({
      severity: editFormProps.editableFields.severity,
      originalSeverity: editFormProps.originalFields?.severity,
      severityReason: editFormProps.editableFields.severityReason,
    });
  const isPublishDisabled =
    isPublished ||
    isArchived ||
    hasUnsavedChanges ||
    isSaving ||
    isProcessing ||
    isPublishing ||
    hasPendingSuggestions;
  const processingStatusText =
    editingFieldCount > 0
      ? t('copilotWorkingEditingFields', { count: editingFieldCount })
      : t('copilotWorkingEditing');

  return (
    <>
      <ObjectEditPageLayout
        eyebrow={eyebrow}
        title={title}
        entityIcon={
          <EntityIcon
            type="finding"
            appearance="highlighted"
            density="compact"
          />
        }
        isProcessing={isProcessing}
        processingAriaLabel={t('copilotSuggestionProcessing')}
        processingStatusText={processingStatusText}
        processingStopLabel={t('copilotWorkingStop')}
        onProcessingStop={onStopCopilotWorking}
        suggestionReviewBar={isProcessing ? null : suggestionReviewBar}
        actions={
          <>
            <MetaDefault color="fg.muted">
              {hasUnsavedChanges ? unsavedChangesLabel : allChangesSavedLabel}
            </MetaDefault>
            <Button
              variant="ghost"
              size="xs"
              onClick={onReset}
              disabled={!hasUnsavedChanges || isSaving || isProcessing}
            >
              {resetLabel}
            </Button>
            <CopyIdButton id={findingId} entityName={entityTitle} />
            <Button
              variant="outline"
              size="xs"
              onClick={onCancel}
              disabled={isSaving || isProcessing}
            >
              {cancelLabel}
            </Button>
            {isPublished ? null : (
              <Button
                variant="outline"
                size="xs"
                onClick={() => setIsPublishConfirmOpen(true)}
                disabled={isPublishDisabled}
                loading={isPublishing}
              >
                {publishLabel}
              </Button>
            )}
            <Button
              variant="solid"
              size="xs"
              colorPalette="accent"
              onClick={onSave}
              disabled={isSaveDisabled}
              loading={isSaving}
            >
              {saveChangesLabel}
            </Button>
          </>
        }
        meta={meta}
      >
        <FindingDetailsEditForm {...editFormProps} />
      </ObjectEditPageLayout>
      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onOpenChange={handlePublishConfirmOpenChange}
        title={t('publishConfirmation.title')}
        description={t('publishConfirmation.message')}
        confirmText={t('publishConfirmation.yes')}
        cancelText={t('publishConfirmation.no')}
        onConfirm={handleConfirmPublish}
        loading={isPublishing}
        variant="warning"
      />
    </>
  );
}
